import { Tooltip, useInterval } from '@timelyapp/react';
import classNames from 'classnames';
import { HOUR_LAYOUT } from 'constants/hour';
import type { TimestampDndItem } from 'constants/timestamps-dnd';
import { TIMESTAMPS_DRAG_DROP_TYPE } from 'constants/timestamps-dnd';
import { DateTime } from 'luxon';
import type { NumberSize, ResizeCallback, ResizeStartCallback } from 're-resizable';
import { Resizable } from 're-resizable';
import type { Direction } from 're-resizable/lib/resizer';
import React, { useCallback, useMemo, useState } from 'react';
import { useDrag } from 'react-dnd';
import FontAwesome from 'react-fontawesome';
import { cssVariables } from 'styles';
import { hexToRgb, isContrastReadable } from 'utils/colors';
import { getDSTTransitionDetails } from 'utils/date-helper';
import { convertTimestampToPx } from 'utils/event';
import { fromISO } from 'utils/luxon-helpers';
import { AdjustResizeHandle } from './AdjustResizeHandle';
import type { AdjustRangeReturn, CalculatedRange } from './ClickDragAdjust';
import styles from './ClickDragAdjust.module.scss';

interface Props {
  currentDate: string;
  range: CalculatedRange;
  offsetTopPx: number;
  offsetLeftPx: number;
  wrapperOffsetLeftPx: number;
  wrapperMarginLeftPx: number;
  pixelsPerMinute: number;
  selectionWidthPx: number;
  indicatorWidthPx: number;
  onClick: () => void;
  onDelete: () => void;
  onDragEnd: () => void;
  onChange: (adjustRangeReturn: AdjustRangeReturn) => void;
  /** Selection/resizing snaps to this number. 1 or larger ONLY */
  snapToMinutes: number;
  showFromToLabels: boolean;
  timeFormat: 12 | 24;
  resizeDirections: Array<
    'top' | 'right' | 'bottom' | 'left' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'
  >;
  children?: React.ReactNode;
  className?: string;
  /**
   * The start of timeline may not be 00:00 of "current day" (like Timely classic).
   * Needed for realtime "HH:MM from px" in AdjustResizeHandle
   */
  pxBeforeMidnight?: number;
  /** Rendered via TIC UI?  */
  hasEnabledTic?: boolean;
}

const dayContainerWidth = 500;

export const Adjust: React.FC<Props> = props => {
  const {
    onClick,
    onChange,
    onDelete,
    onDragEnd,
    currentDate,
    range,
    offsetTopPx,
    offsetLeftPx,
    wrapperOffsetLeftPx,
    wrapperMarginLeftPx,
    snapToMinutes,
    selectionWidthPx,
    pixelsPerMinute,
    showFromToLabels,
    timeFormat,
    resizeDirections = ['top', 'bottom'],
    children,
    pxBeforeMidnight = 0,
    hasEnabledTic = false,
  } = props;

  const [fromY, setFromY] = useState<number | null>(null);
  const [heightPx, setHeightPx] = useState<number | null>(null);
  const [isSelecting, setIsSelecting] = useState<boolean>(false);

  const DSTTransitionDetails = useMemo(
    () => getDSTTransitionDetails(fromISO(currentDate)),
    [currentDate]
  );

  useInterval(60000, () => {
    if (range.live) {
      const now = DateTime.now().toISO();
      const updatedHeight = convertTimestampToPx(now, range.from, 0, {
        pixelsPerMinute,
        hasMinimumHeight: true,
      });

      setHeightPx(updatedHeight);
    }
  });

  const snap = useCallback(
    (px: number) => {
      return Math.round(px / pixelsPerMinute / snapToMinutes) * snapToMinutes * pixelsPerMinute;
    },
    [pixelsPerMinute, snapToMinutes]
  );
  const snapDelta = useCallback(
    (delta: NumberSize, direction: Direction) => {
      const fromY = direction === 'top' ? range.fromY - delta.height : range.fromY;
      const snappedFromY = snap(fromY);
      const adjustedHeight = snap(range.heightPx + delta.height + (fromY - snappedFromY));
      return {
        fromY: snappedFromY,
        heightPx: adjustedHeight,
      };
    },
    [range.fromY, range.heightPx, snap]
  );

  const onResize: ResizeCallback = useCallback(
    (event, direction, refToElement, delta) => {
      const { fromY, heightPx } = snapDelta(delta, direction);

      setFromY(fromY);
      setHeightPx(heightPx);
    },
    [snapDelta]
  );

  const onResizeStart: ResizeStartCallback = useCallback(event => {
    setIsSelecting(true);
    event.preventDefault(); // Prevent ClickDrag from tiggering
  }, []);

  const onResizeStop: ResizeCallback = useCallback(
    (event, direction, refToElement, delta) => {
      const { fromY, heightPx } = snapDelta(delta, direction);

      onChange({
        index: range.index,
        fromY: fromY || range.fromY,
        heightPx,
      });

      setFromY(null);
      setHeightPx(null);
      setIsSelecting(false);
    },
    [snapDelta, range.index, range.fromY, onChange]
  );

  const [, dragRef] = useDrag<TimestampDndItem, unknown, unknown>(
    () => ({
      type: TIMESTAMPS_DRAG_DROP_TYPE,
      item: () => range,
      end: onDragEnd,
    }),
    [range, onDragEnd]
  );

  const resizeConfig = useMemo(
    () => ({
      top: resizeDirections.includes('top') && !range.disabled && !range.live && !range.locked,
      right: resizeDirections.includes('right') && !range.disabled && !range.live && !range.locked,
      bottom:
        resizeDirections.includes('bottom') && !range.disabled && !range.live && !range.locked,
      left: resizeDirections.includes('left') && !range.disabled && !range.live && !range.locked,
      topRight:
        resizeDirections.includes('topRight') && !range.disabled && !range.live && !range.locked,
      bottomRight:
        resizeDirections.includes('bottomRight') && !range.disabled && !range.live && !range.locked,
      bottomLeft:
        resizeDirections.includes('bottomLeft') && !range.disabled && !range.live && !range.locked,
      topLeft:
        resizeDirections.includes('topLeft') && !range.disabled && !range.live && !range.locked,
    }),
    [range.disabled, range.live, range.locked, resizeDirections]
  );

  const resizeHandleStyles = useMemo(
    () => ({ top: { cursor: 'ns-resize' }, bottom: { cursor: 'ns-resize' } }),
    []
  );

  const [textColor, isLightBackground] = useMemo(() => {
    if (range.live) {
      return [range.color || '#000', false];
    } else {
      const defaultBackground = '#6c7076';
      const background = range.color || defaultBackground;
      const isDarkBackground = isContrastReadable(background, '#fff');
      return [isDarkBackground ? '#fff' : '#000', !isDarkBackground];
    }
  }, [range.color, range.live]);

  const heightInPx = heightPx || range.heightPx;
  const widthPx = selectionWidthPx - offsetLeftPx;
  const fromPx = fromY || range.fromY;
  const toPx = fromPx + heightInPx;

  const showTopLabel = showFromToLabels && heightInPx >= 30;
  const showBottomLabel = showFromToLabels && heightInPx >= 50;

  const wrapperStyle: React.CSSProperties = {
    position: 'absolute',
    top: offsetTopPx,
    bottom: 0,
    right: 0,
    left: wrapperOffsetLeftPx,
    marginLeft: wrapperMarginLeftPx,
  };

  const className = classNames(
    styles.adjustContainer,
    {
      [styles.locked]: range.locked,
      [styles.disabled]: range.disabled,
      [styles.live]: range.live,
      [styles.active]: isSelecting,
      [styles.isLightBackground]: isLightBackground,
    },
    props.className
  );
  const opacity = range.locked ? 0 : 1;
  const contentClassName = classNames(styles.adjustContent, {
    [styles.locked]: range.locked,
  });

  const drawBackgroundIndicator = isSelecting || !hasEnabledTic;
  return (
    <div style={wrapperStyle}>
      {/** Needed from `Timeline.Selection` to prevent selection when resizing */}
      <div data-timestamp-adjuster>
        <Resizable
          style={{
            top: fromY || range.fromY,
            backgroundColor: range.color ? `rgba(${hexToRgb(range.color)}, ${opacity})` : undefined,
            color: textColor,
            zIndex: 10,
          }}
          className={className}
          size={{
            // Ensure vertically adjacent timestamps have 1px gap between them
            height: heightInPx - 1,
            width: widthPx,
          }}
          onResize={onResize}
          onResizeStart={onResizeStart}
          onResizeStop={onResizeStop}
          maxHeight={HOUR_LAYOUT.maxHeight * pixelsPerMinute}
          grid={[0, pixelsPerMinute * snapToMinutes]}
          enable={resizeConfig}
          handleStyles={resizeHandleStyles}
          handleComponent={{
            top: (
              <AdjustResizeHandle
                showFromToLabels={showTopLabel}
                direction="top"
                pxToCurrentDayTop={fromPx - pxBeforeMidnight}
                pixelsPerMinute={pixelsPerMinute}
                isSelecting={isSelecting}
                timeFormat={timeFormat}
                DSTTransitionDetails={DSTTransitionDetails}
              />
            ),
            bottom: (
              <AdjustResizeHandle
                showFromToLabels={showBottomLabel}
                direction="bottom"
                pxToCurrentDayTop={toPx - pxBeforeMidnight}
                pixelsPerMinute={pixelsPerMinute}
                isSelecting={isSelecting}
                timeFormat={timeFormat}
                DSTTransitionDetails={DSTTransitionDetails}
              />
            ),
          }}
        >
          <Tooltip
            tooltip="The entry is locked. Unlock day/entry to make changes."
            enabled={range.locked && !!range.hourId}
            childrenClassName={contentClassName}
            className={styles.tooltip}
          >
            <div
              ref={range.locked ? undefined : dragRef}
              className={contentClassName}
              onClick={onClick}
              style={{
                // We need the adjust content to have a background color, otherwise the safari drag preview is transparent
                background: range.color ?? undefined,
              }}
            >
              {children}
            </div>
          </Tooltip>

          {!isSelecting && (
            <div className={styles.deleteButton} onClick={onDelete}>
              <FontAwesome name="times" />
            </div>
          )}
        </Resizable>
        {drawBackgroundIndicator && (
          <div
            className={styles.adjustContainerFullwidthIndicator}
            style={{
              top: fromY || range.fromY,
              left: 0,
              zIndex: 1,
              height: heightInPx,
              width: `calc(100vw - ${dayContainerWidth}px - ${cssVariables.mainNavWidth}px)`,
            }}
          />
        )}
      </div>
    </div>
  );
};
